import { useEffect, useState } from "react";
import { motion } from "motion/react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import { adminStyles } from "./adminStyles";

export default function AdminNoticesPage({
                                             primaryColor = "#8D6E63",
                                             secondaryColor = "#A1887F",
                                         }) {
    const navigate = useNavigate();

    const [notices, setNotices] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState("");

    useEffect(() => {
        loadNotices();
    }, []);

    async function loadNotices() {
        setIsLoading(true);
        setErrorMessage("");

        const { data, error } = await supabase
            .from("site_notices")
            .select("*")
            .order("created_at", { ascending: false });

        if (error) {
            console.error("Fehler beim Laden der Hinweise:", error);
            setErrorMessage("Die Hinweise konnten nicht geladen werden.");
            setIsLoading(false);
            return;
        }

        setNotices(data || []);
        setIsLoading(false);
    }

    async function handleDelete(id) {
        const confirmed = window.confirm(
            "Möchtest du diesen Hinweis wirklich löschen?"
        );

        if (!confirmed) return;

        const { error } = await supabase
            .from("site_notices")
            .delete()
            .eq("id", id);

        if (error) {
            console.error("Fehler beim Löschen:", error);
            alert("Der Hinweis konnte nicht gelöscht werden.");
            return;
        }

        setNotices((current) => current.filter((item) => item.id !== id));
    }

    async function handleToggleActive(item) {
        const nextActiveState = !item.active;

        const { error } = await supabase
            .from("site_notices")
            .update({
                active: nextActiveState,
                updated_at: new Date().toISOString(),
            })
            .eq("id", item.id);

        if (error) {
            console.error("Fehler beim Aktualisieren:", error);
            alert("Der Hinweis konnte nicht aktualisiert werden.");
            return;
        }

        setNotices((current) =>
            current.map((notice) =>
                notice.id === item.id
                    ? {
                        ...notice,
                        active: nextActiveState,
                        updated_at: new Date().toISOString(),
                    }
                    : notice
            )
        );
    }

    const formatDate = (value) => {
        if (!value) return "Nicht gesetzt";

        return new Date(value).toLocaleDateString("de-DE", {
            day: "2-digit",
            month: "long",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    const getTypeLabel = (type) => {
        switch (type) {
            case "success":
                return "Erfolg";
            case "warning":
                return "Warnung";
            case "urgent":
                return "Dringend";
            case "info":
            default:
                return "Info";
        }
    };

    return (
        <main style={adminStyles.dashboardPage}>
            <div style={adminStyles.dashboardContainer}>
                <header style={adminStyles.dashboardHeader}>
                    <div>
                        <h1 style={{ ...adminStyles.title, color: primaryColor }}>
                            Webseiten-Hinweise
                        </h1>
                        <p style={adminStyles.subtitle}>
                            Verwalte kurzfristige Hinweise, die oben auf der Webseite angezeigt werden.
                        </p>
                    </div>

                    <div
                        style={{
                            display: "flex",
                            gap: "0.75rem",
                            flexWrap: "wrap",
                        }}
                    >
                        <button
                            type="button"
                            onClick={() => navigate("/admin")}
                            style={{
                                ...adminStyles.secondaryButton,
                                backgroundColor: secondaryColor,
                            }}
                        >
                            Dashboard
                        </button>

                        <button
                            type="button"
                            onClick={() => navigate("/admin/notices/new")}
                            style={{
                                ...adminStyles.secondaryButton,
                                backgroundColor: primaryColor,
                            }}
                        >
                            Neuer Hinweis
                        </button>
                    </div>
                </header>

                {errorMessage && <div style={adminStyles.error}>{errorMessage}</div>}

                {isLoading ? (
                    <p style={{ color: "#666" }}>Hinweise werden geladen...</p>
                ) : notices.length === 0 ? (
                    <motion.div
                        style={adminStyles.dashboardCard}
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4 }}
                    >
                        <h2 style={{ marginTop: 0, color: primaryColor }}>
                            Noch keine Hinweise vorhanden
                        </h2>
                        <p style={{ color: "#666", lineHeight: 1.7 }}>
                            Lege den ersten Hinweis an, um ihn oben auf der Webseite anzeigen zu lassen.
                        </p>

                        <button
                            type="button"
                            onClick={() => navigate("/admin/notices/new")}
                            style={{
                                ...adminStyles.secondaryButton,
                                backgroundColor: primaryColor,
                                marginTop: "1rem",
                            }}
                        >
                            Hinweis erstellen
                        </button>
                    </motion.div>
                ) : (
                    <div style={{ display: "grid", gap: "1rem" }}>
                        {notices.map((item, index) => (
                            <motion.article
                                key={item.id}
                                style={{
                                    ...adminStyles.dashboardCard,
                                    display: "grid",
                                    gridTemplateColumns: "minmax(0, 1fr) auto",
                                    gap: "1rem",
                                    alignItems: "center",
                                }}
                                initial={{ opacity: 0, y: 18 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{
                                    duration: 0.35,
                                    delay: index * 0.04,
                                }}
                            >
                                <div style={{ minWidth: 0 }}>
                                    <div
                                        style={{
                                            display: "flex",
                                            gap: "0.5rem",
                                            flexWrap: "wrap",
                                            marginBottom: "0.75rem",
                                        }}
                                    >
                                        <span
                                            style={{
                                                display: "inline-block",
                                                padding: "0.3rem 0.75rem",
                                                borderRadius: "999px",
                                                backgroundColor: item.active
                                                    ? `${primaryColor}18`
                                                    : "#f1f1f1",
                                                color: item.active ? primaryColor : "#777",
                                                fontSize: "0.85rem",
                                                fontWeight: 700,
                                            }}
                                        >
                                            {item.active ? "Aktiv" : "Inaktiv"}
                                        </span>

                                        <span
                                            style={{
                                                display: "inline-block",
                                                padding: "0.3rem 0.75rem",
                                                borderRadius: "999px",
                                                backgroundColor: "#f7f7f7",
                                                color: "#666",
                                                fontSize: "0.85rem",
                                                fontWeight: 700,
                                            }}
                                        >
                                            {getTypeLabel(item.type)}
                                        </span>
                                    </div>

                                    <h2
                                        style={{
                                            margin: "0 0 0.5rem",
                                            color: primaryColor,
                                            fontSize: "1.35rem",
                                            lineHeight: 1.35,
                                            wordBreak: "break-word",
                                        }}
                                    >
                                        {item.title || "Hinweis ohne Titel"}
                                    </h2>

                                    <p
                                        style={{
                                            margin: "0 0 0.75rem",
                                            color: "#666",
                                            lineHeight: 1.6,
                                            wordBreak: "break-word",
                                        }}
                                    >
                                        {item.message}
                                    </p>

                                    <p
                                        style={{
                                            margin: 0,
                                            color: "#888",
                                            fontSize: "0.9rem",
                                            lineHeight: 1.6,
                                        }}
                                    >
                                        Start: {formatDate(item.starts_at)} · Ende: {formatDate(item.ends_at)}
                                    </p>
                                </div>

                                <div
                                    style={{
                                        display: "flex",
                                        gap: "0.5rem",
                                        flexWrap: "wrap",
                                        justifyContent: "flex-end",
                                    }}
                                >
                                    <button
                                        type="button"
                                        onClick={() => handleToggleActive(item)}
                                        style={{
                                            ...adminStyles.secondaryButton,
                                            backgroundColor: item.active ? "#777" : primaryColor,
                                        }}
                                    >
                                        {item.active ? "Deaktivieren" : "Aktivieren"}
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() => navigate(`/admin/notices/${item.id}/edit`)}
                                        style={{
                                            ...adminStyles.secondaryButton,
                                            backgroundColor: secondaryColor,
                                        }}
                                    >
                                        Bearbeiten
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() => handleDelete(item.id)}
                                        style={{
                                            ...adminStyles.secondaryButton,
                                            backgroundColor: "#b94a48",
                                        }}
                                    >
                                        Löschen
                                    </button>
                                </div>
                            </motion.article>
                        ))}
                    </div>
                )}
            </div>
        </main>
    );
}